import { Component, Input, OnChanges, SimpleChanges, AfterViewInit, OnInit, OnDestroy, ElementRef, ViewChild, NgZone } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';
import { CommonService } from '../../provide/common.service';

// Add ResizeObserver type definition for TypeScript
interface ResizeObserverCallback {
  (entries: ResizeObserverEntry[], observer: ResizeObserver): void;
}

interface ResizeObserverEntry {
  readonly contentRect: DOMRectReadOnly;
  readonly target: Element;
}

interface ResizeObserver {
  disconnect(): void;
  observe(target: Element): void;
  unobserve(target: Element): void;
}

interface ResizeObserverConstructor {
  new(callback: ResizeObserverCallback): ResizeObserver;
}

declare global {
  interface Window {
    ResizeObserver?: ResizeObserverConstructor;
  }
}

@Component({
    selector: 'app-pdf-viewer',
    template: `
    <div class="pdf-container" [style.height]="containerHeight" [style.width]="containerWidth">
      <div *ngIf="isLoading" class="loading-indicator">
        Loading Document...
      </div>
      <div *ngIf="hasError" class="error-message">
        Error loading document: {{errorMessage}}
      </div>
      
      <!-- Auto-height iframe viewer with no scrolling -->
      <iframe 
        #pdfIframe
        [src]="safeUrl" 
        width="100%" 
        [style.height]="iframeHeight"
        [style.width]="iframeWidth"
        frameborder="0"
        (load)="onIframeLoad()"
        (error)="onError($event)"
        class="pdf-iframe"
        [scrolling]="isPdf ? 'no' : 'yes'">
      </iframe>
    </div>
  `,
    styles: [`
    .pdf-container {
      width: 100%;
      height: 100%;
      margin: auto;
      box-shadow: 0 0 12px rgba(0,0,0,0.1);
      border: 1px solid #ccc;
      position: relative;
      min-height: 800px;
      background: #fff;
    }
    .pdf-iframe {
      border: none;
      width: 100%;
      height: 100%;
      margin-top: -44px;
      -ms-overflow-style: none;
      scrollbar-width: none;
    }
    .pdf-iframe::-webkit-scrollbar {
      display: none;
      width: 0;
      height: 0;
    }
    .loading-indicator {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 20px;
      background: rgba(255,255,255,0.9);
      border-radius: 4px;
      z-index: 10;
    }
    .error-message {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      padding: 20px;
      background: rgba(255,0,0,0.1);
      color: red;
      border-radius: 4px;
      z-index: 10;
      max-width: 80%;
      text-align: center;
    }
  `],
    standalone: false
})
export class PdfViewerComponent implements OnChanges, AfterViewInit, OnInit, OnDestroy {
  @Input() pdfSrc: string = '';
  @Input() documentType: string = '';
  @ViewChild('pdfIframe') pdfIframe: ElementRef;
  
  safeUrl: SafeResourceUrl;
  isLoading: boolean = true;
  hasError: boolean = false;
  errorMessage: string = '';
  containerHeight: string = '100%';
  containerWidth: string = '100%';
  iframeHeight: string = '100%';
  iframeWidth: string = '100%';
  defaultHeight: number = 800;
  resizeObserver: ResizeObserver | null = null;
  hideScrollbarsInterval: any = null;
  isPdf: boolean = false;

  constructor(
    private sanitizer: DomSanitizer,
    private commonService: CommonService,
    private el: ElementRef,
    private ngZone: NgZone
  ) {}

  ngOnInit() {
    this.commonService.pdfViewingState.next(true);
  }

  ngOnDestroy() {
    this.commonService.pdfViewingState.next(false);
    if (this.resizeObserver) {
      this.resizeObserver.disconnect();
    }
    if (this.hideScrollbarsInterval) {
      clearInterval(this.hideScrollbarsInterval);
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes.pdfSrc || changes.documentType) {
      this.isLoading = true;
      this.hasError = false;
      
      this.isPdf = this.documentType.toLowerCase().includes('pdf') || this.pdfSrc.toLowerCase().endsWith('.pdf');

      if (this.pdfSrc) {
        let url = this.pdfSrc;
        if (this.isPdf) {
          url += '#toolbar=0&navpanes=0&scrollbar=0&view=FitH&zoom=page-fit';
        }
        this.safeUrl = this.sanitizer.bypassSecurityTrustResourceUrl(url);
      } else {
        this.hasError = true;
        this.errorMessage = 'No document source provided';
      }
    }
  }

  ngAfterViewInit() {
    // No need to disable page scrolling
  }

  onIframeLoad() {
    this.isLoading = false;

    if (this.isPdf) {
      try {
        const iframe = document.querySelector('.pdf-iframe') as HTMLIFrameElement;
        if (iframe) {
            iframe.style.marginTop = '-44px'; // Apply negative margin for PDF toolbar
        }
        if (iframe && iframe.contentDocument) {
          const style = document.createElement('style');
          style.textContent = `
            html, body {
              margin: 0 !important; /* Force reset margins */
              padding: 0 !important; /* Force reset paddings */
              overflow: hidden;
              height: 100%;
            }

            /* Hide scrollbars for all elements */
            * {
              -ms-overflow-style: none;
              scrollbar-width: none;
              overflow: hidden;
            }

            /* Chrome, Safari, Opera */
            *::-webkit-scrollbar {
              display: none;
              width: 0;
              height: 0;
            }

            body::-webkit-scrollbar { 
              display: none;
              width: 0;
              height: 0;
            } 

            body { 
              -ms-overflow-style: none;
              scrollbar-width: none;
              overflow: hidden;
            }

            /* Hide PDF.js specific scrollbars */
            .pdfViewer, .page, .canvasWrapper, .textLayer {
              overflow: hidden;
            }

            /* Additional PDF.js specific elements */
            #viewerContainer, #viewer, .spread, .pdfViewer, .page {
              overflow: hidden;
              -ms-overflow-style: none;
              scrollbar-width: none;
            }

            #viewerContainer::-webkit-scrollbar, 
            #viewer::-webkit-scrollbar,
            .spread::-webkit-scrollbar,
            .pdfViewer::-webkit-scrollbar,
            .page::-webkit-scrollbar {
              display: none;
              width: 0;
              height: 0;
            }
          `;
          iframe.contentDocument.head.appendChild(style);

          iframe.contentDocument.addEventListener('wheel', this.preventScroll, { passive: false });
          iframe.contentDocument.addEventListener('touchmove', this.preventScroll, { passive: false });

          // This logic is now mostly handled by CSS and iframe properties
          // this.adjustHeightBasedOnContent(iframe);
          // this.setupResizeObserver(iframe);
          // setTimeout(() => {
          //   this.forceIframeRedraw(iframe);
          // }, 100);
        }
      } catch (e) {
        console.warn('Could not access iframe contentDocument to hide scrollbars for PDF:', e);
      }
    } else {
        const iframe = document.querySelector('.pdf-iframe') as HTMLIFrameElement;
        if (iframe) {
            iframe.style.overflow = 'auto';
            iframe.style.height = '100%'; // Ensure iframe fills container vertically
            iframe.style.width = '100%'; // Ensure iframe fills container horizontally
            iframe.style.minHeight = `${this.defaultHeight}px`; // Maintain minimum height
            iframe.style.marginTop = '0'; // Ensure no negative margin for text files

            try {
                if (iframe.contentDocument) {
                    const doc = iframe.contentDocument;
                    doc.documentElement.style.overflow = 'auto'; // Allow scrolling for text
                    doc.body.style.overflow = 'auto'; // Allow scrolling for text
                    doc.documentElement.style.height = '100%'; // Ensure content height adjusts
                    doc.body.style.height = '100%'; // Ensure content height adjusts
                    doc.documentElement.style.margin = '0 !important'; // Force reset margins
                    doc.body.style.margin = '0 !important'; // Force reset margins
                    doc.documentElement.style.padding = '0 !important'; // Force reset paddings
                    doc.body.style.padding = '0 !important'; // Force reset paddings

                    const existingStyle = doc.head.querySelector('style');
                    if (existingStyle) {
                        existingStyle.remove();
                    }
                    doc.removeEventListener('wheel', this.preventScroll);
                    doc.removeEventListener('touchmove', this.preventScroll);
                }
            } catch (e) {
                console.warn('Could not access iframe contentDocument to set scrollbars for text:', e);
            }
        }
    }
    // Set container and iframe heights to 100% to ensure they fill the space
    this.containerHeight = '100%';
    this.containerWidth = '100%';
    this.iframeHeight = '100%';
    this.iframeWidth = '100%';
  }

  onError(error: any) {
    this.isLoading = false;
    this.hasError = true;
    this.errorMessage = `Failed to load document: ${error.message || 'Unknown error'}`;
    console.error('Document loading error:', error);
  }

  private preventScroll(e: Event) {
    e.preventDefault();
  }

  private adjustHeightBasedOnContent(iframe: HTMLIFrameElement) {
    // This method is largely redundant now that we are using 100% height and explicit overflow
    // Keeping it for potential future dynamic height adjustments if needed, but not actively used for 100% fill
    if (iframe && iframe.contentWindow && iframe.contentDocument) {
      try {
        const doc = iframe.contentDocument;
        // Only adjust height if content is smaller than default, otherwise rely on 100%
        const contentScrollHeight = Math.max(doc.body.scrollHeight, doc.documentElement.scrollHeight);
        if (contentScrollHeight > 0 && contentScrollHeight < this.defaultHeight) {
          this.iframeHeight = `${this.defaultHeight}px`;
          this.containerHeight = this.iframeHeight;
          console.log('Adjusted iframe height for smaller content:', this.iframeHeight);
        } else if (contentScrollHeight >= this.defaultHeight) {
          // If content is larger or equal, let CSS 100% height handle it
          this.iframeHeight = '100%';
          this.containerHeight = '100%';
        } else {
          // Fallback if content height is not reliably determined
          this.iframeHeight = `${this.defaultHeight}px`;
          this.containerHeight = this.iframeHeight;
        }
      } catch (e) {
        console.warn('Could not access iframe content for height adjustment (likely cross-origin):', e);
        this.iframeHeight = '100%';
        this.containerHeight = '100%';
      }
    } else {
      this.iframeHeight = '100%';
      this.containerHeight = '100%';
    }
  }

  private setupResizeObserver(iframe: HTMLIFrameElement) {
    // This is also largely redundant for 100% height, but can stay for robustness
    if (window.ResizeObserver && iframe.contentDocument) {
      this.ngZone.runOutsideAngular(() => {
        this.resizeObserver = new window.ResizeObserver(entries => {
          this.ngZone.run(() => {
            // Re-evaluate height if content resizes within the 100% bounds
            this.adjustHeightBasedOnContent(iframe);
          });
        });
        this.resizeObserver.observe(iframe.contentDocument.body);
      });
    } else {
      console.warn('ResizeObserver not supported or iframe content not accessible.');
    }
  }

  private forceIframeRedraw(iframe: HTMLIFrameElement) {
    if (iframe) {
      iframe.style.display = 'none';
      iframe.offsetHeight;
      iframe.style.display = 'block';
    }
  }

  private setupScrollbarHider(iframe: HTMLIFrameElement) {
    // This logic is now integrated into onIframeLoad
  }
} 